import { useCallback, useEffect, useRef, useState } from "react";

type OAuthMessage = {
  type: "facebook_oauth_callback";
  code?: string | null;
  state?: string | null;
  error?: string | null;
};

type Opts = {
  /**
   * Called with the signed state token the opener stored when opening the
   * popup. Return the string you expect to see echoed back; incoming
   * postMessages whose `state` doesn't match are ignored. Returning
   * `null` accepts any state (only useful for the rare legacy caller).
   */
  getExpectedState: () => string | null;
  onCode: (args: { code: string; state: string }) => void | Promise<void>;
  onError?: (message: string) => void;
};

/**
 * Shared Facebook OAuth popup flow. Centralizes the window.open +
 * postMessage + duplicate-suppression plumbing so the "create" flow
 * (FacebookAuthStep) and the "re-auth" flow (AuthorizationDrawer) can
 * share a single, tested implementation.
 *
 * Notes:
 * - Origin is strictly checked against window.location.origin.
 * - The exchange handler fires at most once per popup open, guarded by
 *   a ref so a fast double-message can't cause two exchanges.
 * - The popup-blocked case is surfaced via the returned `error` state
 *   so the host component can render an inline alert consistent with
 *   the rest of its layout.
 */
export function useFacebookOAuthPopup({ getExpectedState, onCode, onError }: Opts) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const exchangedRef = useRef(false);

  const reportError = useCallback(
    (msg: string) => {
      setError(msg);
      setLoading(false);
      onError?.(msg);
    },
    [onError],
  );

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as OAuthMessage | undefined;
      if (!data || data.type !== "facebook_oauth_callback") return;

      const expected = getExpectedState();
      if (expected !== null && data.state !== expected) {
        // Not for this hook instance — another drawer/tab owns this callback.
        return;
      }

      popupRef.current = null;

      if (data.error) {
        reportError(`Facebook authorization failed: ${data.error}`);
        return;
      }
      if (!data.code) {
        reportError("No authorization code received from Facebook.");
        return;
      }
      if (!data.state) {
        reportError("Missing OAuth state. Please try again.");
        return;
      }

      if (exchangedRef.current) return;
      exchangedRef.current = true;

      try {
        await onCode({ code: data.code, state: data.state });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Failed to complete OAuth flow";
        reportError(msg);
        return;
      }

      setLoading(false);
    },
    [getExpectedState, onCode, reportError],
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  /**
   * Open the Facebook OAuth consent popup at `url`. Resets the single-fire
   * guard and clears any prior error. Returns `true` if the popup opened,
   * `false` if the browser blocked it (the hook also reports the error).
   */
  const openPopup = useCallback(
    (url: string): boolean => {
      exchangedRef.current = false;
      setError(null);
      setLoading(true);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "facebook_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      if (!popupRef.current) {
        reportError(
          "Popup was blocked by your browser. Please allow popups for this site and try again.",
        );
        return false;
      }

      const interval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(interval);
          setLoading((prev) => (prev ? false : prev));
        }
      }, 1000);

      return true;
    },
    [reportError],
  );

  return { openPopup, loading, error, clearError: () => setError(null) };
}
